import * as Sentry from "@sentry/browser"
import { type Offering, type OfferingProduct } from "../../features/payment/domain/models/offering"
import {
  OfferingType,
  PriceTypes,
  ProductType,
  type ProductTypeValue,
} from "../../features/payment/domain/models/offering-type"
import { ApiEndpoint } from "../../features/shared/api-endpoint"
import { RetriesExhaustedError, retryWithBackoff, type RetryPolicy } from "./retry"

export type AddProduct = (type: OfferingType, product: OfferingProduct) => void

export interface OfferingSource {
  readonly execute: (offer: string) => Promise<Offering>
}

export const OFFERING_RETRY_POLICY: RetryPolicy = { maxAttempts: 3, baseDelayMs: 500 }

const selectProduct = (offering: Offering): OfferingProduct | undefined => {
  const item = offering.offerings.find((candidate) =>
    Object.values(ProductType).includes(candidate.type as ProductTypeValue)
  )
  return item?.products.find((product) => product.priceType === PriceTypes[item.type])
}

const httpStatusOf = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined
  }
  const { response } = error as { response?: { status?: number } }
  return response?.status
}

const reportOfferingFailure = (productType: OfferingType, failure: unknown): void => {
  const exhausted = failure instanceof RetriesExhaustedError ? failure : undefined
  const error = exhausted ? exhausted.lastError : failure
  Sentry.captureException(error, {
    tags: { context: "Offering fetch failed" },
    extra: {
      endpoint: ApiEndpoint.PaddleOffering,
      productType,
      status: httpStatusOf(error),
      attempts: exhausted?.attempts,
    },
  })
}

/**
 * Fetches every offering, retrying each failed fetch under `policy`, and adds the product each
 * one sells. An offering that fails every attempt is reported to Sentry and left out.
 */
export const loadProducts = async (
  getOffering: OfferingSource,
  addNewProduct: AddProduct,
  policy: RetryPolicy
): Promise<void> => {
  const types = Object.values(OfferingType)
  const results = await Promise.allSettled(
    types.map(async (type) => await retryWithBackoff(async () => await getOffering.execute(type), policy))
  )

  results.forEach((result, index) => {
    if (result.status === "rejected") {
      reportOfferingFailure(types[index], result.reason)
      return
    }
    const product = selectProduct(result.value)
    if (product) {
      addNewProduct(types[index], product)
    }
  })
}
